import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { icons } from './Icons';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  alt?: string;
  name?: string;
  size?: AvatarSize;
  shape?: 'circle' | 'square';
  onImageError?: React.ReactEventHandler<HTMLImageElement>;
}

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-8 w-8 text-sm',
  md: 'h-9 w-9 text-sm',
  lg: 'h-11 w-11 text-base',
  xl: 'h-14 w-14 text-lg',
};

const avatarColors = [
  'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200',
  'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200',
  'bg-primary-50 text-primary-900 dark:bg-primary-800/50 dark:text-primary-100',
  'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200',
];

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getColorFromName = (name: string) => {
  if (!name.trim()) return avatarColors[1];
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) | 0;
  }
  return avatarColors[(hash >>> 0) % avatarColors.length];
};

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ src, alt, name, size = 'md', shape = 'circle', className, onImageError, ...props }, ref) => {
    const [imageFailed, setImageFailed] = useState(false);
    const displayName = name?.trim() ?? '';
    const label = alt || displayName;
    const hasImage = Boolean(src) && !imageFailed;
    const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-md';

    return (
      <div
        ref={ref}
        className={cn(
          'relative inline-flex flex-shrink-0 items-center justify-center overflow-hidden font-semibold',
          shapeClass,
          sizeClasses[size],
          hasImage ? 'bg-neutral-100 dark:bg-neutral-800' : getColorFromName(displayName),
          className
        )}
        role={label ? 'img' : undefined}
        aria-label={label}
        {...props}
      >
        {hasImage ? (
          <img
            src={src as string}
            alt={label}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
            onError={event => {
              setImageFailed(true);
              onImageError?.(event);
            }}
          />
        ) : displayName ? (
          <span className="truncate px-1 text-center" aria-hidden="true">{getInitials(displayName)}</span>
        ) : (
          <icons.User className="h-1/2 w-1/2" aria-hidden="true" />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  max?: number;
  size?: AvatarSize;
  remainingLabel?: (remainingCount: number) => string;
}

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ children, max = 5, size = 'md', remainingLabel, className, ...props }, ref) => {
    const childrenArray = React.Children.toArray(children);
    const visibleMax = Math.max(0, max);
    const visibleChildren = childrenArray.slice(0, visibleMax);
    const remainingCount = Math.max(0, childrenArray.length - visibleMax);

    return (
      <div ref={ref} className={cn('flex flex-shrink-0', className)} {...props}>
        {visibleChildren.map((child, index) =>
          React.isValidElement(child)
            ? React.cloneElement(child as React.ReactElement<AvatarProps>, {
                key: child.key ?? index,
                size,
                className: cn('ring-2 ring-white dark:ring-neutral-900', child.props.className),
              })
            : child
        )}
        {remainingCount > 0 && (
          <span
            className={cn(
              'inline-flex items-center justify-center ring-2 font-semibold',
              'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200',
              'ring-white dark:ring-neutral-900',
              size === 'xs' ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs'
            )}
            role="img"
            aria-label={remainingLabel ? remainingLabel(remainingCount) : `${remainingCount} weitere`}
          >
            +{remainingCount}
          </span>
        )}
      </div>
    );
  }
);

AvatarGroup.displayName = 'AvatarGroup';
